import { useState } from 'react';
import { Link } from 'react-router-dom';
import { PageHeader, Panel, Toolbar } from '../components/Page';
import { EmptyState, ErrorState, TableSkeleton } from '../components/Feedback';
import { DirectionChip, EntityTypeChip, OutcomeChip } from '../components/Chips';
import { SelectField, enumOptions, parseEnumValue } from '../components/Form';
import { ExpandableRow, TableWrap } from '../components/Table';
import { SortableTh } from '../components/SortableTh';
import { Pagination } from '../components/Pagination';
import { history } from '../lib/endpoints';
import { useAsync } from '../lib/hooks';
import { useSortableTable } from '../lib/useSortableTable';
import {
  QboEntityType,
  QboEntityTypeLabels,
  SyncDirection,
  SyncDirectionLabels,
  SyncOutcome,
  SyncOutcomeLabels,
} from '../lib/enums';
import { formatDateTime, formatDuration } from '../lib/format';
import type { SyncHistoryDto } from '../lib/types';

type HistoryColumn = 'when' | 'operation' | 'entity' | 'direction' | 'outcome' | 'document' | 'duration';

function historySortValue(entry: SyncHistoryDto, key: HistoryColumn): string | number {
  switch (key) {
    case 'when':
      return entry.createdUtc;
    case 'operation':
      return entry.operation;
    case 'entity':
      return entry.entityType;
    case 'direction':
      return entry.direction;
    case 'outcome':
      return entry.outcome;
    case 'document':
      return entry.fileName ?? '';
    case 'duration':
      return entry.durationMs ?? 0;
    default:
      return '';
  }
}

export function SyncHistoryPage(): JSX.Element {
  const [outcome, setOutcome] = useState<SyncOutcome | null>(null);
  const [entityType, setEntityType] = useState<QboEntityType | null>(null);
  const [direction, setDirection] = useState<SyncDirection | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [expanded, setExpanded] = useState<number | null>(null);

  const list = useAsync(
    (signal) => history.list({ outcome, entityType, direction, page, pageSize }, signal),
    [outcome, entityType, direction, page, pageSize],
  );

  const historyTable = useSortableTable<HistoryColumn>({
    storageKey: 'syncHistoryColumnWidths',
    columns: {
      when: { minWidth: 140 },
      operation: { minWidth: 120 },
      entity: { minWidth: 110 },
      direction: { minWidth: 100 },
      outcome: { minWidth: 110 },
      document: { minWidth: 140 },
      duration: { minWidth: 90 },
    },
  });

  return (
    <>
      <PageHeader
        title="Sync History"
        description="Every read from and write to QuickBooks Online, with the Intuit request id for support tickets."
        actions={
          <button type="button" className="btn btn-secondary" onClick={list.reload} disabled={list.loading}>
            {list.loading ? 'Refreshing…' : 'Refresh'}
          </button>
        }
      />

      <Panel>
        <Toolbar>
          <SelectField
            label="Outcome"
            value={outcome === null ? '' : String(outcome)}
            onChange={(value) => {
              setOutcome(parseEnumValue<SyncOutcome>(value));
              setPage(1);
            }}
            options={enumOptions(SyncOutcomeLabels)}
            placeholder="All outcomes"
          />
          <SelectField
            label="Entity"
            value={entityType === null ? '' : String(entityType)}
            onChange={(value) => {
              setEntityType(parseEnumValue<QboEntityType>(value));
              setPage(1);
            }}
            options={enumOptions(QboEntityTypeLabels)}
            placeholder="All entities"
          />
          <SelectField
            label="Direction"
            value={direction === null ? '' : String(direction)}
            onChange={(value) => {
              setDirection(parseEnumValue<SyncDirection>(value));
              setPage(1);
            }}
            options={enumOptions(SyncDirectionLabels)}
            placeholder="Both directions"
          />
        </Toolbar>

        {list.loading && !list.data ? (
          <TableSkeleton rows={12} columns={6} />
        ) : list.error ? (
          <ErrorState message="History could not be loaded." detail={list.error} onRetry={list.reload} />
        ) : !list.data || list.data.items.length === 0 ? (
          <EmptyState
            title="No history"
            description="Nothing has been read from or written to QuickBooks with these filters."
          />
        ) : (
          <>
            <TableWrap busy={list.loading} tableClassName="data-table-sortable">
              <colgroup>
                <col style={historyTable.columnStyle('when')} />
                <col style={historyTable.columnStyle('operation')} />
                <col style={historyTable.columnStyle('entity')} />
                <col style={historyTable.columnStyle('direction')} />
                <col style={historyTable.columnStyle('outcome')} />
                <col style={historyTable.columnStyle('document')} />
                <col style={historyTable.columnStyle('duration')} />
              </colgroup>
              <thead>
                <tr>
                  <SortableTh
                    label="When"
                    sortKey="when"
                    currentSortKey={historyTable.sortKey}
                    sortDir={historyTable.sortDir}
                    onSort={historyTable.toggleSort}
                    style={historyTable.columnStyle('when')}
                    onResizeStart={historyTable.beginResize('when')}
                  />
                  <SortableTh
                    label="Operation"
                    sortKey="operation"
                    currentSortKey={historyTable.sortKey}
                    sortDir={historyTable.sortDir}
                    onSort={historyTable.toggleSort}
                    style={historyTable.columnStyle('operation')}
                    onResizeStart={historyTable.beginResize('operation')}
                  />
                  <SortableTh
                    label="Entity"
                    sortKey="entity"
                    currentSortKey={historyTable.sortKey}
                    sortDir={historyTable.sortDir}
                    onSort={historyTable.toggleSort}
                    style={historyTable.columnStyle('entity')}
                    onResizeStart={historyTable.beginResize('entity')}
                  />
                  <SortableTh
                    label="Direction"
                    sortKey="direction"
                    currentSortKey={historyTable.sortKey}
                    sortDir={historyTable.sortDir}
                    onSort={historyTable.toggleSort}
                    style={historyTable.columnStyle('direction')}
                    onResizeStart={historyTable.beginResize('direction')}
                  />
                  <SortableTh
                    label="Outcome"
                    sortKey="outcome"
                    currentSortKey={historyTable.sortKey}
                    sortDir={historyTable.sortDir}
                    onSort={historyTable.toggleSort}
                    style={historyTable.columnStyle('outcome')}
                    onResizeStart={historyTable.beginResize('outcome')}
                  />
                  <SortableTh
                    label="Document"
                    sortKey="document"
                    currentSortKey={historyTable.sortKey}
                    sortDir={historyTable.sortDir}
                    onSort={historyTable.toggleSort}
                    style={historyTable.columnStyle('document')}
                    onResizeStart={historyTable.beginResize('document')}
                  />
                  <SortableTh
                    label="Duration"
                    sortKey="duration"
                    currentSortKey={historyTable.sortKey}
                    sortDir={historyTable.sortDir}
                    onSort={historyTable.toggleSort}
                    className="num"
                    style={historyTable.columnStyle('duration')}
                    onResizeStart={historyTable.beginResize('duration')}
                  />
                </tr>
              </thead>
              <tbody>
                {historyTable.sort(list.data.items, historySortValue).map((entry) => (
                  <ExpandableRow
                    key={entry.id}
                    expanded={expanded === entry.id}
                    onToggle={() => setExpanded((current) => (current === entry.id ? null : entry.id))}
                    colSpan={7}
                    label={`${entry.operation} ${formatDateTime(entry.createdUtc)}`}
                    summary={
                      <>
                        <td>
                          {formatDateTime(entry.createdUtc)}
                          <span className="cell-sub">{entry.performedByEmail ?? 'system'}</span>
                        </td>
                        <td className="mono">{entry.operation}</td>
                        <td>
                          <EntityTypeChip entityType={entry.entityType} />
                        </td>
                        <td>
                          <DirectionChip direction={entry.direction} />
                        </td>
                        <td>
                          <OutcomeChip outcome={entry.outcome} />
                          {entry.errorCode ? <span className="cell-flag warn">{entry.errorCode}</span> : null}
                        </td>
                        <td>{entry.fileName ?? '—'}</td>
                        <td className="num">{formatDuration(entry.durationMs)}</td>
                      </>
                    }
                    detail={
                      <dl className="kv-list kv-cols-3 history-detail">
                        <div className="kv">
                          <dt className="kv-label">Error code</dt>
                          <dd className="kv-value mono">{entry.errorCode ?? '—'}</dd>
                        </div>
                        <div className="kv">
                          <dt className="kv-label">HTTP status</dt>
                          <dd className="kv-value mono">{entry.httpStatusCode ?? '—'}</dd>
                        </div>
                        <div className="kv">
                          <dt className="kv-label">Duration</dt>
                          <dd className="kv-value">{formatDuration(entry.durationMs)}</dd>
                        </div>
                        <div className="kv">
                          <dt className="kv-label">Intuit request id</dt>
                          <dd className="kv-value mono">{entry.intuitRequestId ?? '—'}</dd>
                        </div>
                        <div className="kv">
                          <dt className="kv-label">QuickBooks id</dt>
                          <dd className="kv-value mono">{entry.qboId ?? '—'}</dd>
                        </div>
                        <div className="kv">
                          <dt className="kv-label">Document</dt>
                          <dd className="kv-value">
                            {entry.documentId ? (
                              <Link to={`/purchase-invoices?document=${entry.documentId}`}>
                                {entry.fileName ?? 'Open document'}
                              </Link>
                            ) : (
                              '—'
                            )}
                          </dd>
                        </div>
                        <div className="kv kv-wide">
                          <dt className="kv-label">Message</dt>
                          <dd className="kv-value">{entry.message ?? '—'}</dd>
                        </div>
                      </dl>
                    }
                  />
                ))}
              </tbody>
            </TableWrap>

            <Pagination
              page={list.data.page}
              pageSize={list.data.pageSize}
              totalCount={list.data.totalCount}
              totalPages={list.data.totalPages}
              onPageChange={setPage}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPage(1);
              }}
            />
          </>
        )}
      </Panel>
    </>
  );
}
